import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Upload, ArrowRight, ArrowLeft, Check, X, FileWarning, FileCheck2,
  ClipboardList, Wand2, Download,
} from "lucide-react";
import { DRILLHOLE } from "@/constants/testIds";
import { toast } from "sonner";

const TABLE_LABELS = {
  collar: "Collars",
  survey: "Downhole Surveys",
  lithology: "Lithology",
  assay: "Assays",
};

const TABLE_ORDER = ["collar", "survey", "lithology", "assay"];

const DELIMITER_OPTIONS = [
  { value: "auto", label: "Auto-detect" },
  { value: ",", label: "Comma ," },
  { value: "\t", label: "Tab \\t" },
  { value: ";", label: "Semicolon ;" },
  { value: "|", label: "Pipe |" },
];

const NONE_VALUE = "__none__";

/**
 * SMIN CSV Import Wizard.
 *
 * 4 steps:
 *   1. Upload files (any of collar / survey / lithology / assay)
 *   2. Map each CSV's columns → SMIN fields (auto-suggested on preview)
 *   3. Choose import options (normalize IDs, delimiter override, skip errors)
 *   4. Run dry-run, review validation report, confirm import
 */
export default function DrillholeImportWizard({ projectId, open, onOpenChange, onImported }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  // Per-table state — key = table_type ("collar", "survey", ...)
  const [files, setFiles] = useState({});           // { collar: File, ... }
  const [previews, setPreviews] = useState({});     // { collar: {headers, sample_rows, ...} }
  const [mappings, setMappings] = useState({});     // { collar: {hole_id: "Hole", ...} }
  const [delimiters, setDelimiters] = useState({}); // { collar: "auto" | "," | "\t" }

  const [options, setOptions] = useState({
    normalize_hole_ids: true,
    trim_whitespace: true,
    skip_errors: true,
    hole_id_pad_length: 3,
    default_element: "Cu",
  });

  const [report, setReport] = useState(null);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setStep(1);
      setFiles({});
      setPreviews({});
      setMappings({});
      setDelimiters({});
      setReport(null);
    }
  }, [open]);

  const activeTables = useMemo(
    () => TABLE_ORDER.filter((t) => !!files[t]),
    [files]
  );

  // -------------------- Step 1: File selection --------------------
  const handleFile = (table, file) => {
    if (!file) {
      const { [table]: _, ...rest } = files;
      setFiles(rest);
      const { [table]: __, ...rp } = previews;
      setPreviews(rp);
      return;
    }
    setFiles((prev) => ({ ...prev, [table]: file }));
    setDelimiters((prev) => ({ ...prev, [table]: prev[table] || "auto" }));
  };

  const runPreview = async () => {
    if (!files.collar) {
      toast.error("Collar CSV is required to start");
      return;
    }
    setBusy(true);
    try {
      const newPreviews = {};
      const newMappings = { ...mappings };
      for (const t of activeTables) {
        const fd = new FormData();
        fd.append("table_type", t);
        fd.append("file", files[t]);
        const d = delimiters[t] || "auto";
        if (d !== "auto") fd.append("delimiter", d);
        const { data } = await api.post(
          `/projects/${projectId}/drillholes/import-preview`, fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        newPreviews[t] = data;
        newMappings[t] = { ...(mappings[t] || {}), ...data.suggested_mapping };
      }
      setPreviews(newPreviews);
      setMappings(newMappings);
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  // -------------------- Step 4: Dry-run validation --------------------
  const runValidation = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      TABLE_ORDER.forEach((t) => {
        if (files[t]) fd.append(`${t}_file`, files[t]);
      });
      const payload = {
        options,
      };
      TABLE_ORDER.forEach((t) => {
        if (files[t]) {
          payload[t] = {
            mapping: mappings[t] || {},
            delimiter: delimiters[t] && delimiters[t] !== "auto" ? delimiters[t] : null,
          };
        }
      });
      fd.append("payload", JSON.stringify(payload));
      fd.append("dry_run", "true");
      const { data } = await api.post(
        `/projects/${projectId}/drillholes/import-mapped`, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setReport(data);
      setStep(4);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Validation failed");
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      TABLE_ORDER.forEach((t) => {
        if (files[t]) fd.append(`${t}_file`, files[t]);
      });
      const payload = { options };
      TABLE_ORDER.forEach((t) => {
        if (files[t]) {
          payload[t] = {
            mapping: mappings[t] || {},
            delimiter: delimiters[t] && delimiters[t] !== "auto" ? delimiters[t] : null,
          };
        }
      });
      fd.append("payload", JSON.stringify(payload));
      fd.append("dry_run", "false");
      const { data } = await api.post(
        `/projects/${projectId}/drillholes/import-mapped`, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Imported ${data.imported || 0} + Updated ${data.updated || 0} drillholes`);
      onImported?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  // -------------------- Helpers --------------------
  const setMapping = (table, fieldKey, header) => {
    setMappings((prev) => ({
      ...prev,
      [table]: { ...(prev[table] || {}), [fieldKey]: header === NONE_VALUE ? null : header },
    }));
  };

  const setDelim = (table, delim) => {
    setDelimiters((prev) => ({ ...prev, [table]: delim }));
    // Trigger re-preview on delimiter change if a preview already exists
    if (previews[table]) {
      previewSingle(table, delim);
    }
  };

  const previewSingle = async (table, delim) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("table_type", table);
      fd.append("file", files[table]);
      if (delim && delim !== "auto") fd.append("delimiter", delim);
      const { data } = await api.post(
        `/projects/${projectId}/drillholes/import-preview`, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPreviews((prev) => ({ ...prev, [table]: data }));
      setMappings((prev) => ({ ...prev, [table]: { ...(prev[table] || {}), ...data.suggested_mapping } }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadErrorCsv = () => {
    if (!report) return;
    const lines = ["table,row,error,raw_hole_id"];
    Object.entries(report.errors || {}).forEach(([tbl, errs]) => {
      errs.forEach((e) => {
        const raw = e.raw ? JSON.stringify(e.raw).replace(/"/g, "'") : "";
        const errStr = (e.errors || []).join(" | ").replace(/"/g, "'");
        lines.push(`${tbl},${e.row},"${errStr}","${raw}"`);
      });
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "smin-import-errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadErrorReport = async (format) => {
    if (!report) return;
    try {
      const res = await api.post(
        `/projects/${projectId}/drillholes/import-error-report.${format}`,
        {
          project_name: undefined,
          errors: report.errors || {},
          deep_findings: report.deep_findings || {},
          orphan_hole_ids: report.orphan_hole_ids || {},
        },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `smin_import_errors.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${format.toUpperCase()} export failed`);
    }
  };

  // -------------------- Render --------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="drillhole-wizard"
        className="rounded-none border-border bg-card sm:max-w-4xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            Drillhole Import Wizard
            <Badge variant="outline" className="ml-2 font-mono text-[10px]" data-testid={DRILLHOLE.wizardStep}>
              STEP {step} / 4
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <StepDots step={step} />

        {step === 1 && (
          <Step1Upload
            files={files}
            delimiters={delimiters}
            onFile={handleFile}
            onDelim={setDelim}
          />
        )}

        {step === 2 && (
          <Step2Mapping
            activeTables={activeTables}
            previews={previews}
            mappings={mappings}
            setMapping={setMapping}
            delimiters={delimiters}
            onDelim={setDelim}
          />
        )}

        {step === 3 && (
          <Step3Options options={options} setOptions={setOptions} />
        )}

        {step === 4 && report && (
          <Step4Report
            report={report}
            onDownload={downloadErrorCsv}
            onDownloadXlsx={() => downloadErrorReport("xlsx")}
            onDownloadPdf={() => downloadErrorReport("pdf")}
          />
        )}

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <Button
            variant="outline"
            disabled={step === 1 || busy}
            onClick={() => setStep(step - 1)}
            data-testid={DRILLHOLE.wizardBack}
            className="rounded-none font-mono text-xs uppercase tracking-widest"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
          </Button>
          <div className="text-xs text-muted-foreground font-mono">
            {activeTables.length} file{activeTables.length === 1 ? "" : "s"} loaded
          </div>
          {step === 1 && (
            <Button
              onClick={runPreview}
              disabled={busy || !files.collar}
              data-testid={DRILLHOLE.wizardNext}
              className="rounded-none font-heading tracking-tight"
            >
              {busy ? "Analyzing…" : (<>Preview & map <ArrowRight className="h-3.5 w-3.5 ml-1.5" /></>)}
            </Button>
          )}
          {step === 2 && (
            <Button
              onClick={() => setStep(3)}
              disabled={busy}
              data-testid={DRILLHOLE.wizardNext}
              className="rounded-none font-heading tracking-tight"
            >
              Options <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          )}
          {step === 3 && (
            <Button
              onClick={runValidation}
              disabled={busy}
              data-testid={DRILLHOLE.wizardNext}
              className="rounded-none font-heading tracking-tight"
            >
              {busy ? "Validating…" : (<>Validate <ArrowRight className="h-3.5 w-3.5 ml-1.5" /></>)}
            </Button>
          )}
          {step === 4 && (
            <Button
              onClick={runImport}
              disabled={busy}
              data-testid={DRILLHOLE.wizardImport}
              className="rounded-none font-heading tracking-tight bg-primary"
            >
              {busy ? "Importing…" : (<><Check className="h-3.5 w-3.5 mr-1.5" /> Confirm import</>)}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Sub-components --------------------

function StepDots({ step }) {
  const dots = [1, 2, 3, 4];
  const labels = ["Upload", "Map columns", "Options", "Validate & import"];
  return (
    <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-2 pb-2 border-b border-border">
      {dots.map((n, i) => (
        <div key={n} className="flex-1 flex items-center gap-2">
          <div className={`h-6 w-6 flex items-center justify-center border ${n === step ? "border-primary text-primary" : n < step ? "border-secondary text-secondary" : "border-border"}`}>
            {n < step ? <Check className="h-3 w-3" /> : n}
          </div>
          <span className={n === step ? "text-primary" : ""}>{labels[i]}</span>
          {i < dots.length - 1 && <div className={`flex-1 h-px ${n < step ? "bg-secondary" : "bg-border"}`} />}
        </div>
      ))}
    </div>
  );
}

function Step1Upload({ files, delimiters, onFile, onDelim }) {
  const testIds = {
    collar: DRILLHOLE.wizardCollarInput,
    survey: DRILLHOLE.wizardSurveyInput,
    lithology: DRILLHOLE.wizardLithoInput,
    assay: DRILLHOLE.wizardAssayInput,
  };
  return (
    <div className="space-y-4 py-2">
      <Alert className="rounded-none border-primary/40 bg-primary/5">
        <Upload className="h-4 w-4" />
        <AlertTitle className="font-heading">Import your borehole data</AlertTitle>
        <AlertDescription className="text-xs">
          Upload one CSV per table. Delimiter (comma / TAB / semicolon) is auto-detected — override below if needed.
          Only the Collar file is required.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {TABLE_ORDER.map((tbl) => (
          <div key={tbl} className={`border ${files[tbl] ? "border-primary" : "border-border"} p-4 bg-card`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="smin-label">{TABLE_LABELS[tbl]}</div>
                <div className="text-[10px] text-muted-foreground font-mono uppercase">
                  {tbl === "collar" ? "Required" : "Optional"}
                </div>
              </div>
              {files[tbl] && (
                <FileCheck2 className="h-4 w-4 text-secondary" />
              )}
            </div>
            <input
              type="file"
              accept=".csv,.tsv,.txt"
              data-testid={testIds[tbl]}
              onChange={(e) => onFile(tbl, e.target.files?.[0])}
              className="block w-full text-xs text-foreground file:mr-3 file:py-1.5 file:px-3 file:border file:border-border file:bg-accent file:text-foreground file:font-mono file:text-[10px] file:uppercase file:tracking-widest"
            />
            {files[tbl] && (
              <div className="mt-2 flex items-center gap-2">
                <div className="text-[10px] font-mono text-muted-foreground truncate max-w-[60%]">
                  {files[tbl].name}
                </div>
                <Select
                  value={delimiters[tbl] || "auto"}
                  onValueChange={(v) => onDelim(tbl, v)}
                >
                  <SelectTrigger
                    className="rounded-none h-7 text-[10px] font-mono w-32"
                    data-testid={`${DRILLHOLE.wizardDelimiterSelect}-${tbl}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DELIMITER_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} className="text-xs">
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  onClick={() => onFile(tbl, null)}
                  className="ml-auto text-muted-foreground hover:text-destructive"
                  title="Remove file"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Step2Mapping({ activeTables, previews, mappings, setMapping }) {
  if (activeTables.length === 0) return null;
  return (
    <div className="space-y-6 py-2 max-h-[60vh] overflow-y-auto pr-1">
      <Alert className="rounded-none border-secondary/40 bg-secondary/5">
        <ClipboardList className="h-4 w-4" />
        <AlertTitle className="font-heading">Confirm the column mapping</AlertTitle>
        <AlertDescription className="text-xs">
          We auto-matched common column names. Adjust each dropdown if needed.
          Required fields are marked with <span className="text-destructive font-mono">*</span>.
        </AlertDescription>
      </Alert>

      {activeTables.map((tbl) => {
        const p = previews[tbl];
        if (!p) return null;
        const headers = p.headers || [];
        const fieldSpecs = p.field_specs || [];
        const rowMap = mappings[tbl] || {};

        return (
          <div key={tbl} className="border border-border bg-card">
            <div className="px-4 py-2 border-b border-border flex items-center justify-between bg-accent">
              <div>
                <div className="font-heading tracking-tight text-sm">{TABLE_LABELS[tbl]}</div>
                <div className="text-[10px] font-mono text-muted-foreground uppercase">
                  {p.row_count} rows · delimiter <span className="text-primary">{p.delimiter === "\t" ? "TAB" : `"${p.delimiter}"`}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 p-4">
              {fieldSpecs.map((f) => (
                <div key={f.key} className="flex items-center gap-2 text-xs">
                  <div className="w-32 shrink-0">
                    <div className="font-mono uppercase tracking-widest text-[10px]">
                      {f.label}
                      {f.required && <span className="text-destructive"> *</span>}
                    </div>
                    <div className="text-[9px] text-muted-foreground">{f.type}</div>
                  </div>
                  <Select
                    value={rowMap[f.key] || NONE_VALUE}
                    onValueChange={(v) => setMapping(tbl, f.key, v)}
                  >
                    <SelectTrigger
                      className="rounded-none h-8 flex-1 font-mono text-xs"
                      data-testid={`${DRILLHOLE.wizardMapSelect}-${tbl}-${f.key}`}
                    >
                      <SelectValue placeholder="— unmapped —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE} className="text-xs">— unmapped —</SelectItem>
                      {headers.map((h) => (
                        <SelectItem key={h} value={h} className="text-xs font-mono">{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {/* Preview table */}
            {p.sample_rows?.length > 0 && (
              <div className="border-t border-border overflow-x-auto max-h-40">
                <table className="w-full font-mono text-[10px]">
                  <thead className="bg-accent sticky top-0">
                    <tr>
                      {headers.map((h) => (
                        <th key={h} className="text-left px-2 py-1 whitespace-nowrap border-r border-border">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {p.sample_rows.slice(0, 5).map((row, i) => (
                      <tr key={i} className="border-t border-border">
                        {headers.map((h) => (
                          <td key={h} className="px-2 py-1 whitespace-nowrap border-r border-border text-muted-foreground">
                            {String(row[h] ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Step3Options({ options, setOptions }) {
  const set = (k, v) => setOptions((o) => ({ ...o, [k]: v }));
  return (
    <div className="space-y-4 py-2">
      <Alert className="rounded-none border-border">
        <Wand2 className="h-4 w-4" />
        <AlertTitle className="font-heading">Import options</AlertTitle>
        <AlertDescription className="text-xs">
          Normalize hole IDs to reconcile files where IDs use different zero-padding
          (e.g. <span className="font-mono text-primary">GVCLDD01</span> ↔ <span className="font-mono text-primary">GVCLDD001</span>).
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="border border-border p-3 flex items-start gap-3 cursor-pointer hover:border-primary/50 transition-colors">
          <Checkbox
            checked={options.normalize_hole_ids}
            onCheckedChange={(v) => set("normalize_hole_ids", !!v)}
            data-testid={DRILLHOLE.wizardOptNormalize}
          />
          <div>
            <div className="text-sm font-heading">Normalize hole IDs</div>
            <div className="text-[11px] text-muted-foreground">Trim, upper-case, pad numeric suffix</div>
          </div>
        </label>

        <div className="border border-border p-3">
          <Label className="smin-label">Pad numeric suffix to length</Label>
          <Input
            type="number"
            min={0}
            max={12}
            value={options.hole_id_pad_length}
            onChange={(e) => set("hole_id_pad_length", parseInt(e.target.value || "0", 10))}
            disabled={!options.normalize_hole_ids}
            data-testid={DRILLHOLE.wizardOptPadLen}
            className="rounded-none bg-input font-mono h-8 mt-1"
          />
          <div className="text-[10px] text-muted-foreground mt-1 font-mono">
            e.g. 3 → DD1 → DD001
          </div>
        </div>

        <label className="border border-border p-3 flex items-start gap-3 cursor-pointer hover:border-primary/50 transition-colors">
          <Checkbox
            checked={options.trim_whitespace}
            onCheckedChange={(v) => set("trim_whitespace", !!v)}
            data-testid={DRILLHOLE.wizardOptTrim}
          />
          <div>
            <div className="text-sm font-heading">Trim whitespace</div>
            <div className="text-[11px] text-muted-foreground">Strip leading/trailing spaces from all cells</div>
          </div>
        </label>

        <label className="border border-border p-3 flex items-start gap-3 cursor-pointer hover:border-primary/50 transition-colors">
          <Checkbox
            checked={options.skip_errors}
            onCheckedChange={(v) => set("skip_errors", !!v)}
            data-testid={DRILLHOLE.wizardOptSkipErrors}
          />
          <div>
            <div className="text-sm font-heading">Skip rows with errors</div>
            <div className="text-[11px] text-muted-foreground">Import valid rows even if some fail validation</div>
          </div>
        </label>

        <div className="border border-border p-3">
          <Label className="smin-label">Default element (assays)</Label>
          <Input
            value={options.default_element}
            onChange={(e) => set("default_element", e.target.value)}
            className="rounded-none bg-input font-mono h-8 mt-1 uppercase"
          />
        </div>
      </div>
    </div>
  );
}

function Step4Report({ report, onDownload, onDownloadXlsx, onDownloadPdf }) {
  const totalErrors = Object.values(report.errors || {}).reduce((n, v) => n + v.length, 0);
  const totalOrphans = Object.values(report.orphan_hole_ids || {}).reduce((n, v) => n + v.length, 0);
  const totalRows = Object.values(report.counts || {}).reduce((n, v) => n + (v.rows || 0), 0);
  const totalValid = Object.values(report.counts || {}).reduce((n, v) => n + (v.valid || 0), 0);

  return (
    <div className="space-y-4 py-2" data-testid={DRILLHOLE.wizardReport}>
      <div className="grid grid-cols-3 gap-3">
        <StatBox label="Rows scanned" value={totalRows} accent="primary" />
        <StatBox label="Valid rows" value={totalValid} accent="secondary" />
        <StatBox label="Row errors" value={totalErrors} accent={totalErrors ? "destructive" : "muted"} />
      </div>

      <div className="border border-border">
        <div className="px-3 py-2 bg-accent border-b border-border smin-label">Per-table summary</div>
        <table className="w-full font-mono text-xs">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-3 py-1.5">Table</th>
              <th className="text-right px-3 py-1.5">Rows</th>
              <th className="text-right px-3 py-1.5 text-secondary">Valid</th>
              <th className="text-right px-3 py-1.5 text-destructive">Errors</th>
              <th className="text-left px-3 py-1.5">Delimiter</th>
            </tr>
          </thead>
          <tbody>
            {TABLE_ORDER.filter((t) => report.counts?.[t]).map((t) => {
              const c = report.counts[t];
              const d = report.delimiters?.[t];
              return (
                <tr key={t} className="border-t border-border">
                  <td className="px-3 py-1.5">{TABLE_LABELS[t]}</td>
                  <td className="text-right px-3 py-1.5">{c.rows}</td>
                  <td className="text-right px-3 py-1.5 text-secondary">{c.valid}</td>
                  <td className="text-right px-3 py-1.5 text-destructive">{c.errors}</td>
                  <td className="px-3 py-1.5">{d === "\t" ? "TAB" : `"${d}"`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalOrphans > 0 && (
        <Alert className="rounded-none border-yellow-500/40 bg-yellow-500/5">
          <FileWarning className="h-4 w-4" />
          <AlertTitle className="font-heading">Orphan hole IDs detected</AlertTitle>
          <AlertDescription className="text-xs space-y-1">
            {Object.entries(report.orphan_hole_ids).map(([tbl, ids]) => (
              <div key={tbl}>
                <span className="font-mono text-primary">{tbl}</span>: {ids.length} IDs not in collar
                — <span className="text-muted-foreground">e.g. {ids.slice(0, 5).join(", ")}{ids.length > 5 ? " …" : ""}</span>
              </div>
            ))}
            <div className="text-muted-foreground pt-1">
              Tip: enable <span className="font-mono">Normalize hole IDs</span> or check for padding differences.
            </div>
          </AlertDescription>
        </Alert>
      )}

      {report.deep_findings && Object.keys(report.deep_findings).length > 0 && (
        <DeepFindings findings={report.deep_findings} />
      )}

      {report.warnings?.length > 0 && (
        <div className="border border-border">
          <div className="px-3 py-2 bg-accent border-b border-border smin-label">Warnings</div>
          <ul className="p-3 space-y-1 text-xs font-mono text-muted-foreground">
            {report.warnings.map((w, i) => <li key={i}>• {w}</li>)}
          </ul>
        </div>
      )}

      {totalErrors > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-muted-foreground">
            {totalErrors} rows will be skipped on import. Download the error report to fix and re-upload.
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onDownload}
              data-testid={DRILLHOLE.wizardDownloadErrors}
              className="rounded-none font-mono text-[10px] uppercase tracking-widest"
            >
              <Download className="h-3 w-3 mr-1.5" /> CSV
            </Button>
            <Button
              variant="outline"
              onClick={onDownloadXlsx}
              data-testid="wizard-download-xlsx"
              className="rounded-none font-mono text-[10px] uppercase tracking-widest"
            >
              <Download className="h-3 w-3 mr-1.5" /> Excel
            </Button>
            <Button
              variant="outline"
              onClick={onDownloadPdf}
              data-testid="wizard-download-pdf"
              className="rounded-none font-mono text-[10px] uppercase tracking-widest"
            >
              <Download className="h-3 w-3 mr-1.5" /> PDF
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, accent = "primary" }) {
  const colors = {
    primary: "text-primary border-primary/40",
    secondary: "text-secondary border-secondary/40",
    destructive: "text-destructive border-destructive/40",
    muted: "text-muted-foreground border-border",
  };
  return (
    <div className={`border p-3 ${colors[accent] || colors.primary}`}>
      <div className="smin-label">{label}</div>
      <div className="font-heading text-3xl mt-1">{value}</div>
    </div>
  );
}

const FINDING_LABELS = {
  dip_sign_errors: "Dip sign errors (positive dip = up-hole)",
  azimuth_out_of_range: "Azimuth out of ±360°",
  coord_swap_suspected: "Easting/Northing swap suspected",
  duplicate_collars: "Duplicate collars",
  duplicate_samples: "Duplicate sample intervals",
  missing_coordinates: "Missing coordinates",
  invalid_rl: "Invalid RL/elevation",
  downhole_length_mismatch: "Downhole length mismatch",
  survey_gaps: "Survey gaps > 50 m",
  overlapping_intervals: "Overlapping intervals",
};

function DeepFindings({ findings }) {
  return (
    <div className="border border-yellow-500/60 bg-yellow-500/5" data-testid="deep-findings">
      <div className="px-3 py-2 border-b border-yellow-500/40 flex items-center gap-2">
        <FileWarning className="h-4 w-4 text-yellow-500" />
        <div className="font-heading text-sm tracking-tight">
          Advanced validation — {Object.values(findings).reduce((n, v) => n + v.length, 0)} issue(s) detected
        </div>
      </div>
      <div className="p-3 space-y-3">
        {Object.entries(findings).map(([type, items]) => (
          <div key={type} className="text-xs">
            <div className="font-mono uppercase tracking-widest text-yellow-500 mb-1">
              {FINDING_LABELS[type] || type} ({items.length})
            </div>
            <ul className="font-mono text-[11px] text-muted-foreground space-y-0.5 pl-3 border-l border-yellow-500/40">
              {items.slice(0, 8).map((it, i) => (
                <li key={i}>{it}</li>
              ))}
              {items.length > 8 && (
                <li className="text-yellow-500">… and {items.length - 8} more</li>
              )}
            </ul>
          </div>
        ))}
        <div className="text-[11px] text-muted-foreground pt-2 border-t border-yellow-500/20">
          These are non-blocking warnings — the import will still succeed. Fix them in your source CSVs
          for cleaner datasets on the next import.
        </div>
      </div>
    </div>
  );
}
